import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Label } from '../components/ui/Label';
import { Alert } from '../components/ui/Alert';
import { Dialog, DialogContent, DialogTrigger } from '../components/ui/Dialog';
import { LayoutTemplate } from 'lucide-react';
import { EmptyState } from '../components/EmptyState';
import { PageHeader } from '../components/PageHeader';
import { MockBadge } from '../components/MockBadge';
import { StatusBadge } from '../components/ui/StatusBadge';
import { HtmlImportSheet } from '../components/html-template/HtmlImportSheet';
import { TemplateLibrarySheet } from '../components/TemplateLibrarySheet';
import { apiFetch } from '../lib/api';
import { useAuth } from '../lib/auth';
import { formatDateTime } from '../lib/format';
import { blocksToHtml, createBlock, type FooterDefaults } from '../lib/blocks';
import { useFooterDefaults } from '../lib/useFooterDefaults';
import { ApiError } from '../types/api';
import type { Template, TemplateListItem, TemplatesListResponse } from '../types/api';

function seedBlockJson(footerDefaults: FooterDefaults) {
  return { blocks: [createBlock('text'), createBlock('footer', footerDefaults)] };
}

/** The server's own reasons when it refused the new template (e.g. `template_invalid`), else a generic line. */
function createErrorMessage(err: unknown): string {
  if (err instanceof ApiError && Array.isArray(err.body.errors)) {
    const reasons = err.body.errors
      .map((e) => (e as { message?: unknown }).message)
      .filter((m): m is string => typeof m === 'string');
    if (reasons.length > 0) return `The template could not be created: ${reasons.join(' ')}`;
  }
  return 'The template could not be created.';
}

function CreateTemplateDialog() {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const footerDefaults = useFooterDefaults();

  const mutation = useMutation({
    mutationFn: () => {
      const blockJson = seedBlockJson(footerDefaults);
      return apiFetch<{ template: Template }>('/templates', {
        method: 'POST',
        body: { name, blockJson, html: blocksToHtml(blockJson.blocks) },
      });
    },
    onSuccess: ({ template }) => {
      void queryClient.invalidateQueries({ queryKey: ['templates'] });
      setOpen(false);
      navigate(`/skabeloner/${template.id}`);
    },
  });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button>+ New template</Button>
      </DialogTrigger>
      <DialogContent title="New template">
        <form
          className="flex flex-col gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            mutation.mutate();
          }}
        >
          {mutation.isError ? <Alert>{createErrorMessage(mutation.error)}</Alert> : null}
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="new-template-name">Name</Label>
            <Input
              id="new-template-name"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <Button type="submit" disabled={mutation.isPending}>
            {mutation.isPending ? 'Creating…' : 'Create template'}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function TemplateRow({ template }: { template: TemplateListItem }) {
  const queryClient = useQueryClient();
  const { isEditor } = useAuth();
  const [confirming, setConfirming] = useState(false);

  const deleteMutation = useMutation({
    mutationFn: () => apiFetch(`/templates/${template.id}`, { method: 'DELETE' }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['templates'] });
      setConfirming(false);
    },
  });

  const deleteError =
    deleteMutation.error instanceof ApiError
      ? (deleteMutation.error.body.message ?? 'The template could not be deleted.')
      : deleteMutation.isError
        ? 'The template could not be deleted.'
        : null;

  const usedByText =
    template.usedBy.campaigns === 0 && template.usedBy.automations === 0
      ? 'Not in use'
      : [
          template.usedBy.campaigns > 0 ? `${template.usedBy.campaigns} campaign(s)` : null,
          template.usedBy.automations > 0 ? `${template.usedBy.automations} automation(s)` : null,
        ]
          .filter(Boolean)
          .join(', ');

  return (
    <>
      <tr className="border-t border-border-soft">
        <td className="px-4 py-3">
          <Link to={`/skabeloner/${template.id}`} className="font-medium no-underline text-text">
            {template.name}
          </Link>
        </td>
        <td className="px-4 py-3">
          {template.kind === 'html' ? (
            <StatusBadge tone="accent" icon="</>">
              HTML
            </StatusBadge>
          ) : (
            <StatusBadge tone="neutral" icon="▦">
              Blocks
            </StatusBadge>
          )}
        </td>
        <td className="px-4 py-3 text-muted">{usedByText}</td>
        <td className="px-4 py-3 text-muted">{formatDateTime(template.updatedAt)}</td>
        <td className="px-4 py-3 text-right">
          <div className="flex justify-end gap-2">
            <Button asChild variant="secondary" size="sm">
              <Link to={`/skabeloner/${template.id}`}>Edit</Link>
            </Button>
            {isEditor ? (
              confirming ? (
                <>
                  <Button
                    variant="secondary"
                    size="sm"
                    disabled={deleteMutation.isPending}
                    onClick={() => deleteMutation.mutate()}
                  >
                    {deleteMutation.isPending ? 'Deleting…' : 'Confirm'}
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => setConfirming(false)}>
                    Cancel
                  </Button>
                </>
              ) : (
                <Button variant="ghost" size="sm" onClick={() => setConfirming(true)}>
                  Delete
                </Button>
              )
            ) : null}
          </div>
        </td>
      </tr>
      {deleteError ? (
        <tr>
          <td colSpan={5} className="px-4 pb-3">
            <Alert>{deleteError}</Alert>
          </td>
        </tr>
      ) : null}
    </>
  );
}

export function SkabelonerListPage() {
  const { isEditor } = useAuth();
  const { data, isLoading } = useQuery({
    queryKey: ['templates'],
    queryFn: () => apiFetch<TemplatesListResponse>('/templates'),
  });
  const templates = [...(data?.templates ?? [])].sort((a, b) => b.updatedAt - a.updatedAt);

  return (
    <>
      <PageHeader
        title="Templates"
        badge={<MockBadge />}
        description="Reusable email designs for campaigns and automated flows. Build one from blocks, or import a finished HTML newsletter."
        actions={
          isEditor ? (
            <>
              <TemplateLibrarySheet />
              <HtmlImportSheet />
              <CreateTemplateDialog />
            </>
          ) : null
        }
      />

      {isLoading ? (
        <div className="text-muted">Loading templates…</div>
      ) : templates.length === 0 ? (
        <EmptyState
          icon={<LayoutTemplate />}
          title="No templates yet"
          description="A template is a reusable email design. Build one from blocks, or import a finished HTML newsletter."
          action={isEditor ? <CreateTemplateDialog /> : undefined}
          secondaryAction={
            isEditor ? (
              <>
                <TemplateLibrarySheet />
                <HtmlImportSheet />
              </>
            ) : undefined
          }
        />
      ) : (
        <div className="border border-border rounded-xl overflow-hidden">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="text-left text-xs text-muted uppercase tracking-wide">
                <th className="px-4 py-2.5 font-medium">Name</th>
                <th className="px-4 py-2.5 font-medium">Type</th>
                <th className="px-4 py-2.5 font-medium">Used by</th>
                <th className="px-4 py-2.5 font-medium">Last changed</th>
                <th className="px-4 py-2.5 font-medium" />
              </tr>
            </thead>
            <tbody>
              {templates.map((t) => (
                <TemplateRow key={t.id} template={t} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
